// Квиз: когда, размер, свайпы (форма → цвет), любимые цветы.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api, type Size, type Slot, type Taste } from '../lib/api.ts';
import { useApp } from '../lib/store.tsx';
import { haptic } from '../lib/tg.ts';
import { track } from '../lib/events.ts';
import { hh, rub, weekdayShort } from '../lib/format.ts';
import { CARDS } from '../assets/cards.ts';

// Фото и подписи карточек квиза, опубликованные в админке («Фото» → «Карточки квиза»). Нет фото — рисунок.
type SwipeOverride = { photo?: string | null; thumb?: string | null; name?: string; desc?: string };
let swipeCache: Promise<Record<string, SwipeOverride>> | null = null;
function useSwipeOverrides() {
  const [m, setM] = useState<Record<string, SwipeOverride>>({});
  useEffect(() => {
    swipeCache ??= api.swipe().then((r) => r.cards, () => ({}));
    let on = true;
    void swipeCache.then((x) => on && setM(x));
    return () => {
      on = false;
    };
  }, []);
  return m;
}
import { Icon, StepTop, Spinner, ErrorNote } from '../ui/kit.tsx';

// ── Когда ───────────────────────────────────────────

export function When() {
  const { go, back, flow, setFlow, depth, screen } = useApp();
  const gift = flow.mode === 'gift';
  const [days, setDays] = useState<{ today: string; tomorrow: string } | null>(null);
  const [date, setDate] = useState<string | null>(flow.date);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [todaySlots, setTodaySlots] = useState<Slot[] | null>(null);
  const fromCheckout = screen.name === 'when' && Boolean(screen.returnTo);

  useEffect(() => {
    api
      .slots()
      .then((r) => {
        setDays({ today: r.today, tomorrow: r.tomorrow });
        if (r.date === r.today) setTodaySlots(r.slots);
        else api.slots(r.today).then((t) => setTodaySlots(t.slots));
        if (!date) setDate(r.date);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!date) return;
    setSlots(null);
    api
      .slots(date)
      .then((r) => {
        setSlots(r.slots);
        const keep = r.slots.find((s) => s.from === flow.slotFrom && s.available);
        setFlow({ date, slotFrom: keep ? keep.from : r.slots.find((s) => s.nearest)?.from ?? null });
      })
      .catch((e) => setError(e.message));
  }, [date]);

  const nearestToday = todaySlots?.find((s) => s.nearest);
  const other = date && days && date !== days.today && date !== days.tomorrow;
  const dayBtn = (d: string, title: string, sub: string, disabled = false) => (
    <button type="button" className="tile" disabled={disabled} aria-pressed={date === d} onClick={() => (haptic.pick(), setDate(d))} style={{ flexDirection: 'column', gap: 2, justifyContent: 'center', minHeight: 74, padding: 10, opacity: disabled ? 0.5 : 1 }}>
      <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
      <span className="tiny">{sub}</span>
    </button>
  );

  return (
    <main className="screen">
      <StepTop step={gift ? 3 : 1} total={gift ? 5 : 4} gift={gift} />
      <h1 className="h1">Когда нужен букет?</h1>
      <ErrorNote error={error} />
      {days && (
        <div className="grid3">
          {dayBtn(days.today, 'Сегодня', nearestToday ? `с ${hh(nearestToday.from)}` : 'не успеем', !nearestToday)}
          {dayBtn(days.tomorrow, 'Завтра', weekdayShort(days.tomorrow))}
          <label className="tile" aria-pressed={Boolean(other)} style={{ flexDirection: 'column', gap: 2, justifyContent: 'center', minHeight: 74, padding: 10, position: 'relative', cursor: 'pointer' }}>
            <span style={{ fontSize: 16, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>{other ? weekdayShort(date!) : 'Другой день'}</span>
            <input
              type="date"
              aria-label="Выбрать другой день"
              min={days.today}
              style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%' }}
              onChange={(e) => e.target.value && setDate(e.target.value)}
            />
          </label>
        </div>
      )}
      <div className="stack">
        <span className="label">Во сколько привезти</span>
        {!slots ? (
          <Spinner />
        ) : (
          <div className="grid3">
            {slots.map((s) => (
              <button
                key={s.from}
                type="button"
                className="chip"
                disabled={!s.available}
                aria-pressed={flow.slotFrom === s.from}
                onClick={() => (haptic.pick(), setFlow({ slotFrom: s.from, slotByRecipient: false }))}
                style={{ minHeight: 52, borderRadius: 14, padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', ...(s.available ? {} : { background: 'var(--sand)', borderStyle: 'dashed', color: 'var(--muted)' }) }}
              >
                <span style={{ fontSize: 15 }}>{`${hh(s.from)}–${hh(s.to)}`}</span>
                {(s.nearest || s.full) && <span style={{ fontSize: 11 }}>{s.available ? 'ближайшее' : 'занято'}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="card row" style={{ alignItems: 'flex-start', gap: 10 }}>
        <Icon name="clock" size={18} color="#6B604A" width={1.8} />
        <span className="small" style={{ color: 'var(--ink-2)' }}>
          Час уходит на сборку после оплаты{nearestToday && date === days?.today ? `, поэтому ближайшее окно ${hh(nearestToday.from)}–${hh(nearestToday.to)}` : ''}. Курьер приедет в любой момент внутри окна.
        </span>
      </div>
      <div className="bottom">
        <button type="button" className="btn" disabled={flow.slotFrom === null} onClick={() => {
          track('slot_chosen', { day: date === days?.today ? 'today' : date === days?.tomorrow ? 'tomorrow' : 'other', slot: flow.slotFrom, busy: slots ? slots.filter((s) => !s.available).length : null });
          setFlow({ slotByRecipient: false });
          if (fromCheckout) back();
          else go({ name: 'size' });
        }}>
          {fromCheckout ? 'Готово' : 'Дальше'}
        </button>
        {gift && date && slots?.some((s) => s.available) && (
          <button type="button" className="btn outline" onClick={() => {
            track('slot_by_recipient', { day: date === days?.today ? 'today' : date === days?.tomorrow ? 'tomorrow' : 'other', from: fromCheckout ? 'checkout' : 'flow' });
            setFlow({ date, slotFrom: null, slotByRecipient: true, prefill: flow.prefill ? { ...flow.prefill, recipientMode: 'ask' } : flow.prefill });
            if (fromCheckout) back();
            else go({ name: 'size' });
          }}>
            Отправить в этот день, время уточнить у получателя
          </button>
        )}
      </div>
      <span hidden>{depth}</span>
    </main>
  );
}

// ── Размер ──────────────────────────────────────────

const SIZE_TEXT: Record<Size, string> = { S: 'небольшой и милый', M: 'золотая середина', L: 'чтобы ахнули' };

export function SizeScreen() {
  const { go, flow, setFlow, catalog } = useApp();
  const gift = flow.mode === 'gift';
  const ranges = useMemo(() => {
    const r: Record<Size, [number, number]> = { S: [Infinity, 0], M: [Infinity, 0], L: [Infinity, 0] };
    for (const d of catalog) for (const s of d.sizes) if (s.available) r[s.size] = [Math.min(r[s.size][0], s.price), Math.max(r[s.size][1], s.price)];
    return r;
  }, [catalog]);
  const hint = gift ? { birthday: 'На день рождения обычно берут M. Выглядит щедро, но не чересчур.', love: 'Для романтики обычно берут L. Пусть будет вау.', sorry: 'Для извинения обычно берут M. Заметно, но без пафоса.', thanks: 'Чтобы сказать спасибо, обычно хватает S или M.', just: 'Просто так обычно берут S. Приятный знак внимания.' }[flow.occasion ?? ''] : null;
  const popular: Size = flow.occasion === 'love' ? 'L' : flow.occasion === 'just' ? 'S' : 'M';
  return (
    <main className="screen">
      <StepTop step={gift ? 4 : 2} total={gift ? 5 : 4} gift={gift} />
      <h1 className="h1">Какой размер?</h1>
      {hint && (
        <div className="note pink">
          <Icon name="info" size={18} />
          <span>{hint}</span>
        </div>
      )}
      <div className="stack">
        {(['S', 'M', 'L'] as Size[]).map((s) => (
          <button key={s} type="button" className={`tile${gift ? ' pink' : ''}`} aria-pressed={flow.size === s} onClick={() => (haptic.pick(), setFlow({ size: s }))}>
            <span style={{ width: 36, height: 36, borderRadius: 18, border: '1px solid var(--line-2)', background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--serif)', fontSize: 17, flex: 'none' }}>{s}</span>
            <span className="stack s6" style={{ gap: 0, flex: 1 }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{ranges[s][1] ? `${rub(ranges[s][0]).replace(' ₽', '')} – ${rub(ranges[s][1])}` : '…'}</span>
              <span className="tiny">{SIZE_TEXT[s]}</span>
            </span>
            {gift && s === popular && <span className="badge" style={{ background: 'var(--white)', color: 'var(--pink-ink)' }}>чаще берут</span>}
          </button>
        ))}
      </div>
      <p className="tiny">Цена зависит от состава.</p>
      <div className="bottom">
        <button type="button" className="btn" onClick={() => (track('size_chosen', { size: flow.size, suggested: gift ? flow.size === popular : null }), go({ name: gift ? 'giftHow' : 'swipes' }))}>
          Дальше
        </button>
      </div>
    </main>
  );
}

// ── Свайпы ──────────────────────────────────────────

const FORMS = CARDS.filter((c) => c.stage === 'form');
const COLORS = CARDS.filter((c) => c.stage === 'color');
type Phase = 'introForm' | 'form' | 'introColor' | 'color';

export function Swipes() {
  const { go, flow, setFlow } = useApp();
  const gift = flow.mode === 'gift';
  const [phase, setPhase] = useState<Phase>('introForm');
  const [i, setI] = useState(0);
  const [taste, setTaste] = useState<Taste>({ ...flow.taste, formsLike: [], formsDislike: [], gammasLike: [], gammasDislike: [] });
  const [dx, setDx] = useState(0);
  const [fly, setFly] = useState<0 | 1 | -1>(0);
  const start = useRef<number | null>(null);

  const list = phase === 'form' ? FORMS : COLORS;
  const card = list[i];
  const finish = (t: Taste) => {
    setFlow({ taste: t });
    go({ name: gift ? 'result' : 'flowers' });
  };

  const decide = (v: 1 | -1 | 0, via: 'swipe' | 'button' = 'button') => {
    if (!card) return;
    haptic.tap();
    track('swipe_answered', { step: phase === 'form' ? 'form' : 'color', card: card.id, answer: v === 1 ? 'like' : v === -1 ? 'no' : 'skip', via });
    const key = phase === 'form' ? 'forms' : 'gammas';
    const next = { ...taste } as any;
    if (v === 1) next[`${key}Like`] = [...next[`${key}Like`], card.id];
    if (v === -1) next[`${key}Dislike`] = [...next[`${key}Dislike`], card.id];
    setTaste(next);
    setFly(v === 0 ? 1 : v);
    setTimeout(() => {
      setFly(0);
      setDx(0);
      if (i + 1 < list.length) setI(i + 1);
      else if (phase === 'form') {
        setPhase('introColor');
        setI(0);
      } else finish(next);
    }, 180);
  };

  const q =
    phase === 'form'
      ? i === 0
        ? gift
          ? 'Как такая форма букета?'
          : 'Как тебе такая форма букета?'
        : 'А такая?'
      : i === 0
        ? gift
          ? 'Как такие оттенки?'
          : 'Как тебе такие оттенки?'
        : 'А такие?';

  const formTab = phase === 'introForm' || phase === 'form';
  const intro = phase === 'introForm' || phase === 'introColor';
  return (
    <main className="screen tight">
      <div className="top">
        <BackOrStep onBack={phase === 'introColor' || phase === 'color' ? () => (setPhase('introForm'), setI(0)) : undefined} />
        <div className="tabs" role="group" aria-label="Этап свайпов">
          <span className={formTab ? 'on' : ''}>{formTab && phase === 'form' ? `Форма: ${i + 1} из ${FORMS.length}` : 'Форма'}</span>
          <span className={!formTab ? 'on' : ''}>{phase === 'color' ? `Цвет: ${i + 1} из ${COLORS.length}` : 'Цвет'}</span>
        </div>
      </div>
      <div className="stack s6" style={{ gap: 4 }}>
        {gift && <span className="eyebrow">{flow.relation === 'mom' ? 'Для мамы' : flow.personName ? `Подарок · ${flow.personName}` : 'Подарок'}</span>}
        <h1 className="h1">{intro ? (gift ? 'Пара свайпов, и поймём вкус' : 'Пара свайпов, и поймём твой вкус') : q}</h1>
        {intro && <span className="small">Два коротких шага: форма, потом цвет</span>}
      </div>

      {intro ? (
        <>
          <div className="card stack" style={{ borderRadius: 28, padding: 20, gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--pink-ink)' }}>{phase === 'introForm' ? 'Шаг 1 из 2' : 'Шаг 2 из 2'}</span>
            <h2 className="h2" style={{ fontSize: 24 }}>
              {phase === 'introForm' ? 'Сначала форма букета' : 'Теперь цвет'}
            </h2>
            <p className="p">
              {phase === 'introForm'
                ? 'Покажем 4 букета. Смотри только на то, как они собраны: плотно, свободно, в коробке или из одного вида цветов. Цвет пока не важен.'
                : 'Покажем 5 букетов в разных гаммах. Теперь смотри только на оттенки, форму мы уже запомнили.'}
            </p>
            <div className="wrap" style={{ gap: 6 }}>
              {(phase === 'introForm' ? ['Классический', 'Воздушный', 'В коробке', 'Монобукет'] : ['Пастель', 'Белая', 'Яркая', 'Тёплая', 'Глубокая']).map((t) => (
                <span key={t} style={{ fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap', background: 'var(--bg)', borderRadius: 12, padding: '8px 10px' }}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          <button type="button" className="btn" onClick={() => (setPhase(phase === 'introForm' ? 'form' : 'color'), setI(0))}>
            {phase === 'introForm' ? 'Смотреть формы' : 'Смотреть цвета'}
          </button>
        </>
      ) : (
        <>
          <div
            className="deck"
            onPointerDown={(e) => {
              start.current = e.clientX;
              (e.target as Element).setPointerCapture?.(e.pointerId);
            }}
            onPointerMove={(e) => start.current !== null && setDx(e.clientX - start.current)}
            onPointerUp={() => {
              const d = dx;
              start.current = null;
              if (Math.abs(d) > 90) decide(d > 0 ? 1 : -1, 'swipe');
              else setDx(0);
            }}
            onPointerCancel={() => ((start.current = null), setDx(0))}
          >
            {list[i + 1] && <SwipeCardView card={list[i + 1]} style={{ transform: 'scale(.96) translateY(8px)', opacity: 0.6 }} />}
            {card && (
              <SwipeCardView
                key={card.id}
                card={card}
                style={{
                  transform: fly ? `translateX(${fly * 480}px) rotate(${fly * 18}deg)` : `translateX(${dx}px) rotate(${dx / 18}deg)`,
                  transition: start.current !== null && !fly ? 'none' : 'transform .18s ease-out',
                }}
                hint={dx > 40 ? 'yes' : dx < -40 ? 'no' : null}
              />
            )}
          </div>
          <div className="decide">
            <button type="button" aria-label="Не нравится" onClick={() => decide(-1)}>
              <Icon name="x" size={28} />
            </button>
            <button type="button" className="yes" aria-label="Нравится" onClick={() => decide(1)}>
              <Icon name="heart" size={28} />
            </button>
          </div>
          <button type="button" className="quiet" onClick={() => decide(0)}>
            Не важно
          </button>
        </>
      )}

      <div className="bottom" style={{ gap: 6 }}>
        {gift && (
          <button type="button" className="btn outline" onClick={() => finish(taste)}>
            Вкус не знаю, подберите по поводу
          </button>
        )}
        {!gift && flow.taste.formsLike.length + flow.taste.gammasLike.length > 0 && (
          <button type="button" className="quiet" onClick={() => go({ name: 'result' })}>
            Пропустить, вкус уже знаем
          </button>
        )}
      </div>
    </main>
  );
}

function BackOrStep({ onBack }: { onBack?: () => void }) {
  const { back } = useApp();
  return (
    <button type="button" className="iconbtn" aria-label="Назад" onClick={onBack ?? back}>
      <Icon name="back" />
    </button>
  );
}

function SwipeCardView({ card, style, hint }: { card: (typeof CARDS)[number]; style?: CSSProperties; hint?: 'yes' | 'no' | null }) {
  const o = useSwipeOverrides()[`${card.stage}:${card.id}`] ?? {};
  return (
    <div className="swipecard" style={style}>
      {o.photo ? (
        <div className="pic" style={{ background: card.bg }}>
          <img src={o.photo} alt={card.label} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} draggable={false} />
        </div>
      ) : (
        <div className="pic" style={{ background: card.bg }} dangerouslySetInnerHTML={{ __html: card.svg }} />
      )}
      <div className="txt">
        <span style={{ fontFamily: 'var(--serif)', fontSize: 24 }}>{o.name ?? card.name}</span>
        <span className="small">{o.desc ?? card.desc}</span>
      </div>
      {hint && (
        <div className="ghost" style={{ justifyContent: hint === 'yes' ? 'flex-start' : 'flex-end' }}>
          <span className="badge" style={hint === 'yes' ? {} : { background: 'var(--white)' }}>
            {hint === 'yes' ? 'Нравится' : 'Не моё'}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Любимые цветы ───────────────────────────────────

const FLOWERS: Array<{ id: string; name: string; color: string; bg: string; season?: boolean }> = [
  { id: 'sunflower', name: 'Подсолнухи', color: '#F2B233', bg: '#FDF3E4', season: true },
  { id: 'rose', name: 'Розы', color: '#E5677A', bg: '#FBEFF3' },
  { id: 'spray_rose', name: 'Кустовые розы', color: '#F4A3B5', bg: '#FBEFF3' },
  { id: 'eustoma', name: 'Эустома', color: '#B9A6E3', bg: '#F1ECF2' },
  { id: 'hydrangea', name: 'Гортензия', color: '#8FB3E8', bg: '#EEF3FA' },
  { id: 'chrysanthemum', name: 'Хризантемы', color: '#F4F1E8', bg: '#F3F1EA' },
  { id: 'carnation', name: 'Гвоздики', color: '#F2A7B6', bg: '#FBEFF3' },
  { id: 'eucalyptus', name: 'Эвкалипт', color: '#9DB59A', bg: '#EEF3E6' },
];

export function Flowers() {
  const { go, flow, setFlow } = useApp();
  const over = useSwipeOverrides();
  const t = flow.taste;
  const state = (id: string) => (t.flowersLike.includes(id) ? 1 : t.flowersDislike.includes(id) ? -1 : 0);
  const cycle = (id: string) => {
    haptic.pick();
    const s = state(id);
    const like = t.flowersLike.filter((x) => x !== id);
    const dislike = t.flowersDislike.filter((x) => x !== id);
    if (s === 0) like.push(id);
    if (s === 1) dislike.push(id);
    setFlow({ taste: { ...t, flowersLike: like, flowersDislike: dislike } });
  };
  return (
    <main className="screen">
      <StepTop
        step={4}
        total={4}
        right={
          <button type="button" className="quiet" style={{ color: 'var(--ink)', fontWeight: 600, fontSize: 14, padding: 0 }} onClick={() => go({ name: 'result' })}>
            Пропустить
          </button>
        }
      />
      <div className="stack s6">
        <h1 className="h1">Какие цветы тебя радуют?</h1>
        <p className="small" style={{ margin: 0 }}>
          Необязательно. Нажми на фото один раз, если любишь, и два раза, если точно не надо.
        </p>
      </div>
      <div className="grid2" style={{ gap: 8 }}>
        {FLOWERS.map((f) => {
          const s = state(f.id);
          return (
            <button
              key={f.id}
              type="button"
              aria-pressed={s !== 0}
              aria-label={`${f.name}: ${s === 1 ? 'люблю' : s === -1 ? 'не надо' : 'без разницы'}`}
              onClick={() => cycle(f.id)}
              style={{ border: `1px solid ${s === 1 ? 'var(--pink-line)' : 'var(--line)'}`, background: s === 1 ? 'var(--pink)' : 'var(--white)', borderRadius: 18, padding: 6, display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left', opacity: s === -1 ? 0.6 : 1 }}
            >
              <span style={{ height: 70, borderRadius: 14, background: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                {over[`flower:${f.id}`]?.photo ? (
                  <img src={over[`flower:${f.id}`]!.thumb ?? over[`flower:${f.id}`]!.photo!} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                <svg aria-hidden="true" viewBox="0 0 40 40" width="40" height="40">
                  <g fill={f.color} stroke={f.color === '#F4F1E8' ? '#D9D2BD' : 'none'}>
                    <circle cx="20" cy="10" r="7.5" />
                    <circle cx="29.5" cy="16.9" r="7.5" />
                    <circle cx="25.9" cy="28.1" r="7.5" />
                    <circle cx="14.1" cy="28.1" r="7.5" />
                    <circle cx="10.5" cy="16.9" r="7.5" />
                  </g>
                  <circle cx="20" cy="20" r="6" fill={f.id === 'sunflower' ? '#6B4A1E' : '#FEEB9E'} />
                </svg>
                )}
                {f.season && <span style={{ position: 'absolute', left: 6, top: 6, fontSize: 11, background: 'var(--white)', borderRadius: 8, padding: '2px 6px' }}>Сейчас сезон</span>}
                {s !== 0 && (
                  <span style={{ position: 'absolute', right: 6, top: 6, width: 24, height: 24, borderRadius: 12, background: s === 1 ? 'var(--pink-line)' : 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={s === 1 ? 'heart' : 'x'} size={14} color="#fff" width={2.6} />
                  </span>
                )}
              </span>
              <span style={{ fontSize: 14, fontWeight: 500, padding: '0 4px 4px', textDecoration: s === -1 ? 'line-through' : 'none' }}>{over[`flower:${f.id}`]?.name ?? f.name}</span>
            </button>
          );
        })}
      </div>
      <div className="bottom" style={{ gap: 4 }}>
        <button type="button" className="btn" onClick={() => (track('flowers_answered', { likes: flow.taste.flowersLike.length, nopes: flow.taste.flowersDislike.length }), go({ name: 'result' }))}>
          Показать мои букеты
        </button>
        <button type="button" className="quiet" onClick={() => go({ name: 'result' })}>
          Пропустить
        </button>
      </div>
    </main>
  );
}
